import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useReactiveVar } from '@apollo/client';
import RequestSearch from '../../../components/common/SearchText';
import RequestList from './RequestList';
import { graphql } from '../../../__generated__';
import { duplicatedTransaction, refreshRequestsVar } from '../common';
import { pollServer } from '../../../graphql/utils';
import useCommonNavigations from '../../../hooks/Navigation';
import { useLocation } from 'react-router';

export const agentTransactionsQuery = graphql(/* GraphQL */ `
  query GetTransactions($searchText: String, $pageSize: Int, $startPage: Int) {
    ...RequestsList_QueryFragment
  }
`);

const AgentRequests = () => {
  const [text, setText] = React.useState<string>('');
  const onValueChange = (value: string) => {
    setText(value);
  };
  const { data, loading, refetch, startPolling, stopPolling } = useQuery(
    agentTransactionsQuery,
    {
      variables: { searchText: text, pageSize: 50, startPage: 0 },
    }
  );

  React.useEffect(() => {
    refetch({
      searchText: text,
      pageSize: 50,
      startPage: 0,
    });
  }, [text, refetch]);

  const shouldRefresh = useReactiveVar(refreshRequestsVar);
  React.useEffect(() => {
    if (shouldRefresh) {
      refreshRequestsVar(false);
      pollServer(startPolling, stopPolling);
    }
  }, [shouldRefresh, startPolling, stopPolling]);

  const location = useLocation();
  const { goToAgentRequest } = useCommonNavigations();

  const highlightRecipient = text.length > 0;

  return (
    <Container fixed>
      <Box m={1}>
        <RequestSearch
          placeholderText="Search..."
          value={text}
          onValueChange={onValueChange}
          onClear={() => setText('')}
        />
      </Box>
      {loading && (
        <Box m={1}>
          <LinearProgress />
        </Box>
      )}
      {highlightRecipient && (
        <Typography
          variant="subtitle2"
          sx={{ mt: 0.25, ml: 0.125, fontSize: 12 }}
        >
          Search results
        </Typography>
      )}
      {data && (
        <RequestList
          query={data}
          loading={loading}
          highlightRecipient={highlightRecipient}
          setSearchText={setText}
        />
      )}
      <Fab
        sx={{ position: 'fixed', bottom: 50, right: 50 }}
        aria-label={'Add'}
        color={'primary'}
        disabled={loading}
        onClick={() => {
          duplicatedTransaction(null);
          goToAgentRequest(location.pathname);
        }}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default AgentRequests;
